import React from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";

const projects = [
  {
    label: "Platform 01",
    status: "Active",
    title: "Bambereaux Integrated Development Platform",
    location: "Saint Vincent & the Grenadines",
    sectors: ["Luxury Residential", "Hospitality", "Healthcare"],
    summary:
      "Flagship integrated development combining luxury residential, hospitality and healthcare infrastructure on the south coast of Saint Vincent — designed as a destination platform with measurable community impact.",
    metric: "US$158M+",
    metricLabel: "Projected platform value",
    image: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/0owokzqi_IMG_0620.jpeg",
    to: "/bambereaux",
    testId: "project-bambereaux",
  },
  {
    label: "Platform 02",
    status: "Active",
    title: "National Agro-Industrial Import Substitution Platform",
    location: "Saint Vincent & the Grenadines",
    sectors: ["Food Sovereignty", "Tomato Processing", "CARICOM Export"],
    summary:
      "Vertically integrated agro-industrial platform anchoring food sovereignty, farmer participation and regional export capacity through a national tomato value chain.",
    metric: "120 Acres",
    metricLabel: "Initial cultivation footprint",
    image: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/2pownbf7_IMG_0624.jpeg",
    to: "/agro-industrial-platform",
    testId: "project-agro",
  },
];

export default function Projects() {
  return (
    <>
      <PageHeader
        testId="projects-header"
        eyebrow="Projects"
        title="A portfolio of strategic platforms."
        intro="Each Evergreen platform addresses a distinct national priority while delivering institutional-grade investment characteristics. The portfolio is designed for measured, deliberate expansion."
        image="https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/lhpfuj5n_IMG_0643.jpeg"
        overlayTone="strong"
      />

      <section data-testid="projects-list" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12 pb-24" data-reveal>
            <div className="lg:col-span-5">
              <div className="evg-overline mb-8">Portfolio Overview</div>
              <h2 className="evg-display-lg">Current strategic platforms.</h2>
            </div>
            <div className="lg:col-span-7">
              <p className="evg-body">
                Evergreen sponsors a curated portfolio of platforms across development &amp;
                infrastructure, food security &amp; agro-industry, and healthcare &amp; community
                impact. The portfolio architecture is deliberately scalable, allowing additional
                platforms to be added without compromising governance or sponsor focus.
              </p>
            </div>
          </div>

          <div className="space-y-28 md:space-y-40">
            {projects.map((p, idx) => (
              <article
                key={p.title}
                data-testid={p.testId}
                data-reveal
                className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-10 items-center"
              >
                <Link
                  to={p.to}
                  className={`lg:col-span-7 evg-img-zoom block ${idx % 2 ? "lg:order-2" : ""}`}
                >
                  <div className="relative aspect-[5/4] overflow-hidden bg-[#0A1F44]">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </Link>
                <div className={`lg:col-span-5 ${idx % 2 ? "lg:order-1" : ""}`}>
                  <div className="flex items-baseline gap-x-6 mb-6">
                    <span className="evg-overline-gold">{p.label}</span>
                    <span className="evg-meta">{p.status}</span>
                  </div>
                  <h3 className="evg-display-md">{p.title}</h3>
                  <p className="evg-meta mt-4">{p.location}</p>
                  <p className="evg-body mt-7">{p.summary}</p>
                  <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
                    {p.sectors.map((s) => (
                      <span key={s} className="evg-meta">
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="mt-12 pt-10 border-t border-[#0A1F44]/12">
                    <div className="evg-overline">{p.metricLabel}</div>
                    <div className="font-serif text-5xl md:text-6xl mt-3 text-[#0A1F44] tracking-tight">
                      {p.metric}
                    </div>
                    <Link
                      to={p.to}
                      data-testid={`${p.testId}-cta`}
                      className="mt-10 inline-block text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors"
                    >
                      View Platform →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Forward Pipeline */}
          <div className="mt-32 pt-16 border-t border-[#0A1F44]/12 grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-10" data-reveal>
            <div className="lg:col-span-5">
              <div className="evg-overline mb-8">Forward Pipeline</div>
              <h3 className="evg-display-md">Additional platforms under evaluation.</h3>
            </div>
            <div className="lg:col-span-7">
              <p className="evg-body">
                Evergreen evaluates additional opportunities across infrastructure, energy
                transition, healthcare and food systems where governance, scale and national
                strategic alignment can be demonstrated. The portfolio architecture supports
                seamless addition of new platforms without redesign of the sponsor framework.
              </p>
              <Link
                to="/investor-relations"
                data-testid="projects-engagement-cta"
                className="mt-10 inline-block text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors"
              >
                Discuss engagement →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
